import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { Badge, Button, Card, EmptyState, Input, Select, Text } from '../components/atoms';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { Section } from '../components/templates/Section/Section';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { AdminArea } from '../components/admin/AdminArea';
import { AdminTable } from '../components/admin/AdminTable';
import { AdminConfirmDialog, type AdminConfirmRequest } from '../components/admin/AdminConfirmDialog';
import { PendingApprovals } from '../components/admin/PendingApprovals';
import { useAuth } from '../context/AuthContext';
import {
  approvalStatusOf,
  approveUser,
  deactivateUser,
  deleteUserAccount,
  fetchAdminUsers,
  reactivateUser,
  resetUserMfa,
  revokeLicense,
  setAdminRole,
  type AdminUser,
  type AdminUserStatusFilter,
} from '../services/adminService';
import { formatDate, formatDateTime, licenseStatusTone } from '../components/admin/adminFormat';

const usersKey = (filter: AdminUserStatusFilter) => ['admin', 'users', filter] as const;

/** Filters of the main list ('pending' has its own section above it). */
const FILTERS: readonly AdminUserStatusFilter[] = ['all', 'deactivated', 'rejected'];

/**
 * Admin → Users: role, licence, account status, MFA reset and account
 * erasure for every user (GET/POST/DELETE /api/v1/admin/users…, licence
 * revoke via DELETE /api/v1/admin/licenses/:id). Every destructive action is
 * confirmed in a dialog; deleting an account needs the e-mail typed in.
 * Closed beta: the "Freigabe ausstehend" section on top lists the accounts
 * waiting for approval; rejected accounts can be found with the filter and
 * approved later.
 */
export function AdminUserManagement() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { state } = useAuth();
  const ownEmail = state.user?.email?.toLowerCase() ?? '';

  const [filter, setFilter] = useState<AdminUserStatusFilter>('all');
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState<AdminConfirmRequest | null>(null);
  const [done, setDone] = useState('');

  const { data, isLoading, isError, refetch, isRefetching } = useQuery({
    queryKey: usersKey(filter),
    queryFn: () => fetchAdminUsers(filter),
  });

  const users = data?.users ?? [];
  const query = search.trim().toLowerCase();
  const visible = query
    ? users.filter((u) => u.email.toLowerCase().includes(query) || u.role.toLowerCase().includes(query))
    : users;

  function ask(request: Omit<AdminConfirmRequest, 'onConfirm'>, action: () => Promise<void>, doneMessage: string): void {
    setDone('');
    setConfirm({
      ...request,
      onConfirm: async () => {
        await action();
        setDone(doneMessage);
        await queryClient.invalidateQueries({ queryKey: ['admin'] });
      },
    });
  }

  function statusLabel(user: AdminUser): string {
    const approval = approvalStatusOf(user);
    if (approval === 'pending') return t('adminArea.users.approval.statusPending');
    if (approval === 'rejected') return t('adminArea.users.approval.statusRejected');
    switch (user.status) {
      case 'active':
        return t('adminArea.users.status.active');
      case 'deactivated':
        return t('adminArea.users.status.deactivated');
      case 'anonymized':
        return t('adminArea.users.status.anonymized');
      default:
        return t('adminArea.users.status.inactive');
    }
  }

  function renderLicense(user: AdminUser) {
    const license = user.license ?? null;
    if (license === null) return <Text variant="caption" className="rg-admin-table__muted">{t('adminArea.users.noLicense')}</Text>;
    return (
      <>
        <Badge tone={licenseStatusTone(license.status)}>{t(`adminArea.licenseStatus.${license.status}`)}</Badge>
        <Text variant="caption" className="rg-admin-table__muted">
          {license.expires_at
            ? t('adminArea.users.licenseUntil', { date: formatDate(license.expires_at, i18n.language) })
            : t('adminArea.users.licenseUnlimited')}
        </Text>
      </>
    );
  }

  function renderActions(user: AdminUser) {
    const isSelf = user.email.toLowerCase() === ownEmail;
    const isAdmin = user.role === 'admin';
    const deactivated = user.status === 'deactivated' || user.status === 'inactive';
    const email = user.email;
    const license = user.license ?? null;

    const approval = approvalStatusOf(user);

    return (
      <div className="rg-admin-table__actions">
        {approval !== 'approved' && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              ask(
                {
                  title: t('adminArea.users.approval.approve'),
                  message: t('adminArea.users.approval.confirmApprove', { email }),
                  confirmLabel: t('adminArea.users.approval.approve'),
                },
                () => approveUser(user.id),
                t('adminArea.users.approval.done.approved', { email }),
              )
            }
          >
            {t('adminArea.users.approval.approve')}
          </Button>
        )}
        {!isSelf && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              ask(
                {
                  title: isAdmin ? t('adminArea.users.removeAdmin') : t('adminArea.users.makeAdmin'),
                  message: t(isAdmin ? 'adminArea.users.confirmRemoveAdmin' : 'adminArea.users.confirmMakeAdmin', { email }),
                  confirmLabel: isAdmin ? t('adminArea.users.removeAdmin') : t('adminArea.users.makeAdmin'),
                },
                () => setAdminRole(user.id, !isAdmin),
                t('adminArea.users.done.role', { email }),
              )
            }
          >
            {isAdmin ? t('adminArea.users.removeAdmin') : t('adminArea.users.makeAdmin')}
          </Button>
        )}
        {license !== null && license.status === 'active' && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              ask(
                {
                  title: t('adminArea.users.revokeLicense'),
                  message: t('adminArea.users.confirmRevokeLicense', { email }),
                  confirmLabel: t('adminArea.users.revokeLicense'),
                },
                () => revokeLicense(license.id),
                t('adminArea.users.done.licenseRevoked', { email }),
              )
            }
          >
            {t('adminArea.users.revokeLicense')}
          </Button>
        )}
        {user.mfa_enabled === true && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() =>
              ask(
                {
                  title: t('adminArea.users.resetMfa'),
                  message: t('adminArea.users.confirmResetMfa', { email }),
                  confirmLabel: t('adminArea.users.resetMfa'),
                },
                () => resetUserMfa(user.id),
                t('adminArea.users.done.mfaReset', { email }),
              )
            }
          >
            {t('adminArea.users.resetMfa')}
          </Button>
        )}
        {!isSelf && user.status !== 'anonymized' && (
          deactivated ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                ask(
                  {
                    title: t('adminArea.users.reactivate'),
                    message: t('adminArea.users.confirmReactivate', { email }),
                    confirmLabel: t('adminArea.users.reactivate'),
                  },
                  () => reactivateUser(user.id),
                  t('adminArea.users.done.reactivated', { email }),
                )
              }
            >
              {t('adminArea.users.reactivate')}
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                ask(
                  {
                    title: t('adminArea.users.deactivate'),
                    message: t('adminArea.users.confirmDeactivate', { email }),
                    confirmLabel: t('adminArea.users.deactivate'),
                  },
                  () => deactivateUser(user.id),
                  t('adminArea.users.done.deactivated', { email }),
                )
              }
            >
              {t('adminArea.users.deactivate')}
            </Button>
          )
        )}
        {!isSelf && user.status !== 'anonymized' && (
          <Button
            variant="danger"
            size="sm"
            onClick={() =>
              ask(
                {
                  title: t('adminArea.users.delete'),
                  message: t('adminArea.users.confirmDelete', { email }),
                  confirmLabel: t('adminArea.users.deleteConfirm'),
                  requireText: email,
                },
                () => deleteUserAccount(user.id),
                t('adminArea.users.done.deleted', { email }),
              )
            }
          >
            {t('adminArea.users.delete')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="wide">
          <PageHeader title={t('adminArea.title')} description={t('adminArea.users.description')} />
          <AdminArea>
      <PendingApprovals ask={ask} />

      <Section title={t('adminArea.users.listTitle', { count: data?.total ?? 0 })}>
        <Card padding="md">
          <div className="rg-admin-form">
            <Select
              label={t('adminArea.users.filterLabel')}
              value={filter}
              onChange={(e) => setFilter(FILTERS.find((f) => f === e.target.value) ?? 'all')}
              options={[
                { value: 'all', label: t('adminArea.users.filterAll') },
                { value: 'deactivated', label: t('adminArea.users.filterDeactivated') },
                { value: 'rejected', label: t('adminArea.users.approval.filterRejected') },
              ]}
              fullWidth
            />
            <Input
              type="search"
              label={t('adminArea.users.searchLabel')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              fullWidth
            />
          </div>
        </Card>

        <LiveRegion>{done}</LiveRegion>

        {isLoading && (
          <LiveRegion>
            <Text>{t('adminArea.users.loading')}</Text>
          </LiveRegion>
        )}

        {isError && (
          <LoadErrorState
            title={t('adminArea.users.loadError')}
            onRetry={() => void refetch()}
            retrying={isRefetching}
          />
        )}

        {!isLoading && !isError && visible.length === 0 && (
          <EmptyState
            icon={<Users size={48} strokeWidth={1.5} aria-hidden="true" />}
            headline={query ? t('adminArea.users.emptySearchTitle') : t('adminArea.users.emptyTitle')}
            description={query ? t('adminArea.users.emptySearchMessage') : t('adminArea.users.emptyMessage')}
            actionLabel={query ? t('adminArea.users.clearSearch') : t('adminArea.reload')}
            onAction={query ? () => setSearch('') : () => void refetch()}
            headingLevel={3}
          />
        )}

        {!isLoading && !isError && visible.length > 0 && (
          <AdminTable caption={t('adminArea.users.tableCaption')}>
            <thead>
              <tr>
                <th scope="col">{t('adminArea.users.col.email')}</th>
                <th scope="col">{t('adminArea.users.col.role')}</th>
                <th scope="col">{t('adminArea.users.col.status')}</th>
                <th scope="col">{t('adminArea.users.col.license')}</th>
                <th scope="col">{t('adminArea.users.col.mfa')}</th>
                <th scope="col">{t('adminArea.users.col.createdAt')}</th>
                <th scope="col">{t('adminArea.users.col.lastLogin')}</th>
                <th scope="col">{t('adminArea.users.col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((user) => (
                <tr key={user.id}>
                  <th scope="row">{user.email}</th>
                  <td>
                    <Badge tone={user.role === 'admin' ? 'info' : 'neutral'}>
                      {user.role === 'admin' ? t('adminArea.users.roleAdmin') : t('adminArea.users.roleUser')}
                    </Badge>
                  </td>
                  <td>
                    <Badge tone={user.status === 'active' && approvalStatusOf(user) === 'approved' ? 'success' : 'warning'}>
                      {statusLabel(user)}
                    </Badge>
                  </td>
                  <td>{renderLicense(user)}</td>
                  <td>{user.mfa_enabled ? t('adminArea.users.mfaOn') : t('adminArea.users.mfaOff')}</td>
                  <td className="rg-admin-table__numeric">{formatDate(user.created_at, i18n.language)}</td>
                  <td className="rg-admin-table__numeric" data-testid="admin-user-last-login">
                    {user.last_login_at ? (
                      <time dateTime={user.last_login_at}>{formatDateTime(user.last_login_at, i18n.language)}</time>
                    ) : (
                      t('adminArea.users.neverLoggedIn')
                    )}
                  </td>
                  <td>{renderActions(user)}</td>
                </tr>
              ))}
            </tbody>
          </AdminTable>
        )}
      </Section>

      <AdminConfirmDialog request={confirm} onClose={() => setConfirm(null)} />
          </AdminArea>
        </PageContent>
      }
    />
  );
}

export default AdminUserManagement;
